import { useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, InputAdornment, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "react-admin";
import { useNavigate } from "react-router-dom";

import {
  CustomerServiceCrmSearchDocument,
  type CustomerServiceCrmSearchQuery,
  type CustomerServiceCrmSearchQueryVariables,
} from "../../generated/graphql";
import { useCrmLabels } from "../../hooks/useCrmLabels";
import { CRM_COLORS, type CrmRecentRecord, crmRecordPath, readRecentCrmRecords } from "../../lib/crm";
import { CrmObjectIcon } from "./CrmObjectIcon";
import { DeletedInSalesforceBadge } from "./DeletedInSalesforceBadge";

const MIN_TERM_LENGTH = 2;
const DEBOUNCE_MS = 250;

type Option = CrmRecentRecord & {
  subtitle?: string | null;
  deletedInSalesforceAt?: string | null;
  recent: boolean;
};

const useDebounced = (value: string): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [value]);
  return debounced;
};

// The Salesforce global search: every object at once, grouped by object, and
// the recently viewed records while nothing is typed. A hit Salesforce deleted
// carries the badge. Ctrl+K or Cmd+K focuses it.
export const CrmGlobalSearch = () => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const term = useDebounced(input.trim());
  const searching = term.length >= MIN_TERM_LENGTH;
  const { data, loading, error } = useQuery<CustomerServiceCrmSearchQuery, CustomerServiceCrmSearchQueryVariables>(
    CustomerServiceCrmSearchDocument,
    { variables: { term }, skip: !searching, fetchPolicy: "cache-first" }
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const options: Option[] = useMemo(() => {
    if (!searching) return open ? readRecentCrmRecords().map(record => ({ ...record, recent: true })) : [];
    return (data?.customerServiceCrmSearch ?? []).map(hit => ({ ...hit, recent: false }));
  }, [data, open, searching]);

  const noOptionsText = () => {
    if (input.trim().length > 0 && input.trim().length < MIN_TERM_LENGTH) return translate("crm.search.tooShort");
    if (loading || input.trim() !== term) return translate("crm.search.loading");
    if (error) return translate("crm.loadError");
    return translate("crm.search.noResults");
  };

  return (
    <Autocomplete<Option, false, false, false>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      filterOptions={candidates => candidates}
      groupBy={option => (option.recent ? translate("crm.search.recent") : labels.object(option.object, true))}
      getOptionLabel={option => option.title}
      isOptionEqualToValue={(option, value) =>
        option.object === value.object && option.salesforceId === value.salesforceId
      }
      inputValue={input}
      onInputChange={(_event, value, reason) => {
        if (reason !== "reset") setInput(value);
      }}
      value={null}
      onChange={(_event, option) => {
        if (!option) return;
        setInput("");
        setOpen(false);
        inputRef.current?.blur();
        navigate(crmRecordPath(option.object, option.salesforceId));
      }}
      noOptionsText={noOptionsText()}
      loading={searching && loading}
      loadingText={translate("crm.search.loading")}
      blurOnSelect
      sx={{ width: { xs: "100%", md: 440 }, maxWidth: "100%", minWidth: 0 }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.object}-${option.salesforceId}`} sx={{ gap: "10px" }}>
          <CrmObjectIcon object={option.object} size="small" />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", color: CRM_COLORS.text }} noWrap>
                {option.title}
              </Typography>
              {option.deletedInSalesforceAt && (
                // A long title shrinks, the badge keeps its full label.
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  <DeletedInSalesforceBadge deletedAt={option.deletedInSalesforceAt} />
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }} noWrap>
              {[labels.object(option.object), option.subtitle].filter(Boolean).join(" • ")}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          inputRef={inputRef}
          size="small"
          placeholder={translate("crm.search.placeholder")}
          inputProps={{ ...params.inputProps, "aria-label": translate("crm.search.label") }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {!input && (
                  <Typography component="kbd" sx={{ fontSize: "11px", color: CRM_COLORS.label, mr: "4px" }}>
                    {translate("crm.search.shortcut")}
                  </Typography>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { backgroundColor: CRM_COLORS.cardBackground, borderRadius: "4px", fontSize: "13px" },
          }}
        />
      )}
    />
  );
};
